import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartConfig } from '../../ui/chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import type { CallVolumeDayPoint } from '../../../services/api';
import { LABELS } from './labels';
import { dayLabel, tooltipLabel } from './format';

interface CallVolumeChartProps {
  /** Chart heading (e.g. "Anrufe je Tag"). */
  title: string;
  /** Full gap-free day series for the month. */
  data: CallVolumeDayPoint[];
  /** Which value of each day point to plot. */
  dataKey: 'count' | 'maxParallel';
  /** Bar colour (hex). */
  color: string;
}

/**
 * Reusable bar chart for one call-volume metric (per-day count or peak parallel).
 * Mirrors the structure of the dashboard's `MetricsChart` but renders bars.
 */
export function CallVolumeChart({ title, data, dataKey, color }: CallVolumeChartProps) {
  const chartConfig: ChartConfig = { [dataKey]: { label: title, color } };
  const chartData = data.map((point) => ({
    ...point,
    label: dayLabel(point.date),
    tooltipLabel: tooltipLabel(point.date),
  }));
  const hasData = data.some((point) => point[dataKey] > 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex items-center justify-center" style={{ height: '33.33vw', maxHeight: '300px' }}>
            <p className="text-gray-500">{LABELS.states.empty}</p>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="w-full" style={{ height: '33.33vw', maxHeight: '300px' }}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                fontSize={12}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                fontSize={12}
                allowDecimals={false}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(_, payload) =>
                      payload && payload.length > 0 ? payload[0].payload.tooltipLabel : ''
                    }
                  />
                }
              />
              <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
